'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useLayoutCodes, useRoutePageIds } from '@/components/ads/AdDataProvider';
import { useDevice } from '@/contexts/DeviceContext';
import { initGPT } from '@/lib/gpt-loader';
import { isLighthouseClient } from '@/lib/isLighthouse';

type PageType = 'home' | 'section' | 'article';

function detectPageType(pathname: string): PageType {
  if (pathname === '/' || pathname === '') return 'home';
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 5) return 'article';
  if (segments.length >= 1) return 'section';
  return 'home';
}

/**
 * Resolve page_id from pathname using route patterns.
 */
function resolvePageId(pathname: string, routePageIds: Record<string, string>): string | null {
  if (routePageIds[pathname]) return routePageIds[pathname];
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length; i >= 1; i--) {
    const pattern = '/' + segments.slice(0, i - 1).concat('{slug}').join('/');
    if (routePageIds[pattern]) return routePageIds[pattern];
  }
  if (segments.length === 1 && routePageIds['/{slug}']) return routePageIds['/{slug}'];
  return null;
}

/**
 * Check if the current page matches any specificPageCodes entry.
 * Matches by: route page_id (UUID), page name (as slug), or page id in URL.
 */
function findSpecificPageMatch(
  specificPageCodes: any[],
  pathname: string,
  currentPageId: string | null
): any | null {
  if (!specificPageCodes || !Array.isArray(specificPageCodes)) return null;

  for (const entry of specificPageCodes) {
    if (!entry.pages || !Array.isArray(entry.pages)) continue;
    const matches = entry.pages.some((p: any) => {
      if (!p) return false;
      // Match by route page_id (UUID from server)
      if (currentPageId && p.id && p.id === currentPageId) return true;
      // Match by page name converted to slug
      if (p.name) {
        const slug = p.name.toLowerCase().replace(/\s+/g, '-');
        if (pathname === `/${slug}` || pathname.startsWith(`/${slug}/`)) return true;
      }
      // Match by page id in URL path
      if (p.id && (pathname === `/${p.id}` || pathname.startsWith(`/${p.id}/`))) return true;
      return false;
    });
    if (matches) return entry;
  }
  return null;
}

/**
 * Get the header and body code strings for a layout code entry
 * based on scope, page type, device type, and specific page overrides.
 */
function getCodeStrings(
  code: Record<string, any>,
  pageType: PageType,
  isMobile: boolean,
  pathname: string = '/',
  currentPageId: string | null = null
): { headerCode: string; bodyCode: string } {
  const scope = code.scope;
  const useMobile = code.hasSeparateMobileCode && isMobile;

  // Check for specificPageCodes override
  if (code.hasSpecificPageCodes && code.specificPageCodes) {
    const match = findSpecificPageMatch(code.specificPageCodes, pathname, currentPageId);
    if (match) {
      return {
        headerCode: (useMobile ? (match.mobileHeaderCode || match.headerCode) : match.headerCode) || '',
        bodyCode: (useMobile ? (match.mobileBodyCode || match.bodyCode) : match.bodyCode) || '',
      };
    }
  }

  if (scope === 'all_pages') {
    return {
      headerCode: (useMobile ? code.mobileHeaderCode : code.headerCode) || '',
      bodyCode: (useMobile ? code.mobileBodyCode : code.bodyCode) || '',
    };
  }

  // page_based scope
  const prefix = useMobile ? 'mobile' : '';
  const pageKey =
    pageType === 'home' ? 'homepage' :
    pageType === 'section' ? 'sectionPage' :
    'articlePage';

  const headerKey = useMobile
    ? `${prefix}${pageKey.charAt(0).toUpperCase() + pageKey.slice(1)}HeaderCode`
    : `${pageKey}HeaderCode`;
  const bodyKey = useMobile
    ? `${prefix}${pageKey.charAt(0).toUpperCase() + pageKey.slice(1)}BodyCode`
    : `${pageKey}BodyCode`;

  return {
    headerCode: code[headerKey] || '',
    bodyCode: code[bodyKey] || '',
  };
}

/**
 * Parse HTML string, extract scripts and non-script content.
 * Header codes → document.head, Body codes → body container div.
 * Returns injected elements for cleanup.
 */
function injectHtml(
  html: string,
  target: 'head' | 'body',
  bodyContainer: HTMLDivElement | null
): HTMLElement[] {
  if (!html.trim()) return [];

  const injected: HTMLElement[] = [];
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const scripts = tempDiv.querySelectorAll('script');
  const nonScriptHtml = html.replace(/<script[\s\S]*?<\/script>/gi, '');

  // Inject non-script content
  if (nonScriptHtml.trim()) {
    if (target === 'head') {
      // For head: inject style/link/meta tags
      const wrapper = document.createElement('div');
      wrapper.innerHTML = nonScriptHtml;
      wrapper.setAttribute('data-layout-code', 'true');
      Array.from(wrapper.children).forEach((child) => {
        const el = child.cloneNode(true) as HTMLElement;
        el.setAttribute('data-layout-code', 'true');
        document.head.appendChild(el);
        injected.push(el);
      });
    } else if (bodyContainer) {
      // For body: append to container div
      const contentDiv = document.createElement('div');
      contentDiv.innerHTML = nonScriptHtml;
      contentDiv.setAttribute('data-layout-code', 'true');
      bodyContainer.appendChild(contentDiv);
      injected.push(contentDiv);
    }
  }

  // Execute scripts — header codes go to document.head, body codes go to body container.
  // Dynamically-inserted external scripts default to `async = true` per the HTML spec, so
  // an inline init that depends on a preceding external lib will run before the lib loads.
  // Force `async = false` so the browser executes them in document order.
  scripts.forEach((script) => {
    const newScript = document.createElement('script');
    Array.from(script.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });
    if (script.src) {
      newScript.src = script.src;
      // Only override async if the source script didn't explicitly set it.
      if (!script.hasAttribute('async')) {
        newScript.async = false;
      }
    } else if (script.textContent) {
      newScript.textContent = script.textContent;
    }
    newScript.setAttribute('data-layout-code', 'true');

    if (target === 'head') {
      document.head.appendChild(newScript);
    } else if (bodyContainer) {
      bodyContainer.appendChild(newScript);
    } else {
      document.body.appendChild(newScript);
    }
    injected.push(newScript);
  });

  return injected;
}

/**
 * LayoutCodeLoader — Injects dynamic layout codes into pages.
 * Reads layout codes from AdDataProvider context.
 * Handles all_pages/page_based scopes, mobile codes, and AMP exclusion.
 * Re-evaluates on client-side navigation (pathname change).
 */
export default function LayoutCodeLoader() {
  const layoutCodes = useLayoutCodes();
  const routePageIds = useRoutePageIds();
  const pathname = usePathname();
  const bodyContainerRef = useRef<HTMLDivElement>(null);
  const injectedElementsRef = useRef<HTMLElement[]>([]);
  const hasInjectedRef = useRef(false);
  // Hash of what was last injected. If the next computed hash matches, we skip
  // cleanup+reinject entirely — prevents CLS/reflow/TBT spike on every SPA navigation.
  const lastInjectionHashRef = useRef<string>('');
  const lastPathnameRef = useRef<string>('');
  // Tracks ALL googletag slots created during the most recent injectAll() —
  // including out-of-page anchor slots whose elements don't carry `div-gpt-ad-` ids.
  // Without this, anchor slots survive cleanup, so re-injection's defineSlot() returns
  // null and the ad never refreshes on client-side navigation.
  const layoutSlotsRef = useRef<any[]>([]);
  const { isMobile, deviceDetected } = useDevice();

  /**
   * Compute a stable hash of the codes that WOULD be injected for the current route.
   * If this matches the previous hash, nothing changed and we can skip re-injection.
   */
  function computeInjectionHash(): string {
    const codes = Object.values(layoutCodes);
    if (codes.length === 0) return '';
    const pageType = detectPageType(pathname);
    const currentPageId = resolvePageId(pathname, routePageIds);
    const parts: string[] = [];
    for (const code of codes) {
      if (code.isActive === false || code.isActive === 'false' || !code.isActive) continue;
      const { headerCode, bodyCode } = getCodeStrings(code, pageType, isMobile, pathname, currentPageId);
      // Include slug (or name) so separate entries with identical empty codes don't collide
      parts.push(`${code.slug || code.name || ''}|${headerCode.length}|${bodyCode.length}|${headerCode}|${bodyCode}`);
    }
    return parts.join('§');
  }

  /**
   * Cleanup: destroy GPT slots and remove injected DOM elements
   */
  function cleanup() {
    // Destroy every slot we tracked from the previous injection (covers both
    // in-container `div-gpt-ad-` slots and out-of-page anchor slots).
    if (typeof window !== 'undefined') {
      const prevDivIds: string[] = [];
      if (bodyContainerRef.current) {
        bodyContainerRef.current.querySelectorAll('[id^="div-gpt-ad-"]').forEach(el => {
          prevDivIds.push(el.id);
        });
      }
      injectedElementsRef.current.forEach(el => {
        if (el.id?.startsWith('div-gpt-ad-')) prevDivIds.push(el.id);
      });

      const trackedSlots = layoutSlotsRef.current;
      if (prevDivIds.length > 0 || trackedSlots.length > 0) {
        const googletag = (window as any).googletag;
        if (googletag) {
          googletag.cmd = googletag.cmd || [];
          googletag.cmd.push(() => {
            try {
              const allSlots = googletag.pubads().getSlots();
              const matchingByDiv = allSlots.filter(
                (slot: any) => prevDivIds.includes(slot.getSlotElementId())
              );
              const slotsToDestroy = Array.from(new Set([...trackedSlots, ...matchingByDiv]));
              if (slotsToDestroy.length > 0) {
                googletag.destroySlots(slotsToDestroy);
              }
            } catch { /* ignore */ }
          });
        }
      }
      layoutSlotsRef.current = [];
    }

    injectedElementsRef.current.forEach((el) => {
      el.parentNode?.removeChild(el);
    });
    injectedElementsRef.current = [];

    if (bodyContainerRef.current) {
      bodyContainerRef.current.innerHTML = '';
    }
  }

  /**
   * Inject all active layout codes for the current page
   */
  function injectAll() {
    const codes = Object.values(layoutCodes);
    if (codes.length === 0) return;

    // Ensure GPT script is loading
    initGPT();

    // Snapshot existing googletag slots so we can identify which ones this
    // injection creates (including out-of-page anchors that have no div id
    // we can match by selector).
    let preExistingSlots: any[] = [];
    if (typeof window !== 'undefined') {
      const googletag = (window as any).googletag;
      try {
        if (googletag?.pubads) {
          preExistingSlots = googletag.pubads().getSlots();
        }
      } catch { /* ignore */ }
    }

    const pageType = detectPageType(pathname);
    const allInjected: HTMLElement[] = [];
    const layoutSlotDivIds: string[] = [];
    let hasAdsenseScript = false;

    for (const code of codes) {
      if (code.isActive === false || code.isActive === 'false' || !code.isActive) continue;

      const currentPageId = resolvePageId(pathname, routePageIds);
      const { headerCode, bodyCode } = getCodeStrings(code, pageType, isMobile, pathname, currentPageId);

      if (headerCode) {
        if (/adsbygoogle\.js/i.test(headerCode)) hasAdsenseScript = true;
        const els = injectHtml(headerCode, 'head', null);
        allInjected.push(...els);
      }

      if (bodyCode) {
        if (/adsbygoogle\.js/i.test(bodyCode)) hasAdsenseScript = true;
        const divIdMatches = bodyCode.match(/id=['"]([^'"]+)['"]/g);
        if (divIdMatches) {
          divIdMatches.forEach(match => {
            const id = match.replace(/id=['"]|['"]/g, '');
            if (id.startsWith('div-gpt-ad-')) {
              layoutSlotDivIds.push(id);
            }
          });
        }

        const els = injectHtml(bodyCode, 'body', bodyContainerRef.current);
        allInjected.push(...els);
      }
    }

    // After scripts execute, capture any new slots they created so the next
    // cleanup can destroy them — including out-of-page anchor slots.
    if (typeof window !== 'undefined') {
      (window as any).googletag = (window as any).googletag || { cmd: [] };
      (window as any).googletag.cmd.push(() => {
        const googletag = (window as any).googletag;
        try {
          const allSlots = googletag.pubads().getSlots();
          const newSlots = allSlots.filter((slot: any) => !preExistingSlots.includes(slot));
          if (newSlots.length > 0) {
            layoutSlotsRef.current = newSlots;
          }
        } catch { /* ignore */ }
      });
    }

    // Refresh GPT slots that match div ids we found in the body code
    if (layoutSlotDivIds.length > 0 && typeof window !== 'undefined') {
      (window as any).googletag = (window as any).googletag || { cmd: [] };
      (window as any).googletag.cmd.push(() => {
        const googletag = (window as any).googletag;
        const allSlots = googletag.pubads().getSlots();
        const layoutSlots = allSlots.filter((slot: any) =>
          layoutSlotDivIds.includes(slot.getSlotElementId())
        );
        if (layoutSlots.length > 0) {
          googletag.pubads().refresh(layoutSlots);
        }
      });
    }

    // For AdSense layout codes, push an empty config so any new <ins> elements render.
    if (hasAdsenseScript && typeof window !== 'undefined') {
      try {
        (window as any).adsbygoogle = (window as any).adsbygoogle || [];
        (window as any).adsbygoogle.push({});
      } catch { /* ignore */ }
    }

    injectedElementsRef.current = allInjected;
    hasInjectedRef.current = true;
  }

  // Main injection effect — only re-inject when the actual content to inject changes.
  // Skipping no-op re-injections removes per-navigation CLS, forced reflow, and TBT spikes.
  useEffect(() => {
    if (!deviceDetected) return;
    if (pathname.includes('/amp/') || pathname.endsWith('/amp')) return;
    if (document.querySelector('[data-hide-layout]')) return;
    // Defensive client guard: skip layout-code injection during Lighthouse runs
    if (isLighthouseClient()) return;

    const nextHash = computeInjectionHash();
    const pathnameChanged = lastPathnameRef.current !== pathname;

    // Skip re-injection only when pathname hasn't changed (e.g. layoutCodes
    // object reference churned but content is identical). On real navigation
    // we always re-inject so scripts (AdSense, GPT anchor, trackers) run again.
    if (!pathnameChanged && nextHash === lastInjectionHashRef.current && hasInjectedRef.current) {
      return;
    }

    // Determine whether this is the very first injection in this loader instance
    // (component just mounted) or a subsequent navigation. Mount → inject
    // synchronously. Navigation → defer two animation frames so the new page
    // DOM has rendered before init scripts (slider/interstitial) run.
    const isFirstInjection = !hasInjectedRef.current && lastPathnameRef.current === '';

    cleanup();
    hasInjectedRef.current = false;
    lastInjectionHashRef.current = nextHash;
    lastPathnameRef.current = pathname;

    if (isFirstInjection) {
      injectAll();
      return;
    }

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        injectAll();
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [pathname, layoutCodes, isMobile, deviceDetected]);

  // Retry: if layout codes didn't inject after 1.5s (GPT timing), try again.
  // Only retry when the body code actually expects a `div-gpt-ad-` div — anchor-only
  // codes have no such div even on success, so blindly retrying causes double-injection.
  useEffect(() => {
    if (!deviceDetected) return;
    if (pathname.includes('/amp/') || pathname.endsWith('/amp')) return;

    const timer = setTimeout(() => {
      if (!bodyContainerRef.current) return;
      const expectedHash = computeInjectionHash();
      if (!expectedHash) return;

      const codes = Object.values(layoutCodes);
      const pageType = detectPageType(pathname);
      const currentPageId = resolvePageId(pathname, routePageIds);
      const expectsGptDiv = codes.some((code) => {
        if (code.isActive === false || code.isActive === 'false' || !code.isActive) return false;
        const { bodyCode } = getCodeStrings(code, pageType, isMobile, pathname, currentPageId);
        return /id=['"]div-gpt-ad-/i.test(bodyCode || '');
      });
      if (!expectsGptDiv) return;

      const gptDivs = bodyContainerRef.current.querySelectorAll('[id^="div-gpt-ad-"]');
      if (gptDivs.length === 0) {
        cleanup();
        hasInjectedRef.current = false;
        injectAll();
        lastInjectionHashRef.current = expectedHash;
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [deviceDetected, pathname]);

  // Don't render on AMP pages
  if (pathname.includes('/amp/') || pathname.endsWith('/amp')) return null;

  // Don't render if no layout codes
  if (Object.keys(layoutCodes).length === 0) return null;

  // `contain: layout` isolates reflows inside this container from the rest of the page.
  // Layout codes typically inject tracking pixels or position:fixed interstitials, so
  // they shouldn't need to affect outer layout — this prevents "Forced reflow" warnings.
  return (
    <div
      ref={bodyContainerRef}
      suppressHydrationWarning
      style={{ contain: 'layout style' }}
    />
  );
}
